import React from 'react';
import Heroes from './Heroes';
import HeroSelectionDialog from './HeroSelectionDialog';
import { searchCharacter, searchTeam, searchStoryArc } from './comicDataProvider';
import { makeStyles } from '@material-ui/core/styles';
import Fab from '@material-ui/core/Fab';
import AddIcon from '@material-ui/icons/Add';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import MenuItem from '@material-ui/core/MenuItem';
import Snackbar from '@material-ui/core/Snackbar';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import InfoIcon from '@material-ui/icons/Info';

// Fallback color for info snackbar (Material UI blue)
const INFO_COLOR = '#2196f3';

const useStyles = makeStyles(theme => ({
  fabContainer: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    zIndex: 1000,
    [theme.breakpoints.down('xs')]: {
      bottom: '16px',
      right: '16px'
    }
  },
  fab: {
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingRight: theme.spacing(1)
  },
  searchField: {
    marginBottom: theme.spacing(2)
  },
  snackbarContent: {
    backgroundColor: (theme.palette.info && theme.palette.info.main) || INFO_COLOR
  },
  snackbarMessage: {
    display: 'flex',
    alignItems: 'center'
  },
  snackbarIcon: {
    marginRight: theme.spacing(1)
  }
}));

const Form = (props) => {
  const classes = useStyles();
  const [userName, setUserName] = React.useState('');
  const [publisher, setPublisher] = React.useState('marvel');
  const [resourceType, setResourceType] = React.useState('character'); // 'character', 'team', or 'storyarc'
  const [searchResults, setSearchResults] = React.useState([]);
  const [showDialog, setShowDialog] = React.useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = React.useState(false);
  const [noResultsOpen, setNoResultsOpen] = React.useState(false);
  const [searchedName, setSearchedName] = React.useState('');
  const [currentSearchName, setCurrentSearchName] = React.useState('');
  const [currentPublisher, setCurrentPublisher] = React.useState('');
  const [currentResourceType, setCurrentResourceType] = React.useState('character');

  // Helper function to create a custom character entry
  const createCustomEntry = (name, pub, resType = 'character') => {
    // Generate a more robust unique ID using timestamp, random value, and name hash
    const timestamp = Date.now();
    const randomPart = Math.random().toString(36).substring(2, 11);
    const nameHash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const uniqueId = `custom-${timestamp}-${randomPart}-${nameHash}`;
    
    return { 
      id: uniqueId, 
      name: name, 
      source: 'custom', 
      publisher: pub,
      resourceType: resType
    };
  };

  const handleSubmit = async () => {
    // Validate that userName is not empty
    if (!userName.trim()) {
      return;
    }
    
    // Store the search parameters for use in cancel handler
    setCurrentSearchName(userName);
    setCurrentPublisher(publisher);
    setCurrentResourceType(resourceType);
    
    try {
      let results = [];
      
      // If resource type is 'character', do sequential search: character -> team -> story arc
      if (resourceType === 'character') {
        // Try character first
        results = await searchCharacter(userName, publisher);
        
        // If no character results, try team
        if (!results || results.length === 0) {
          console.log('No character found, searching teams...');
          results = await searchTeam(userName, publisher);
          
          if (results && results.length > 0) {
            console.log(`Found ${results.length} team(s) matching "${userName}"`);
          } else {
            // If no team results, try story arc
            console.log('No team found, searching story arcs...');
            results = await searchStoryArc(userName, publisher);
            
            if (results && results.length > 0) {
              console.log(`Found ${results.length} story arc(s) matching "${userName}"`);
            }
          }
        } else {
          console.log(`Found ${results.length} character(s) matching "${userName}"`);
        }
      } else if (resourceType === 'team') {
        // Direct team search
        results = await searchTeam(userName, publisher);
        if (results && results.length > 0) {
          console.log(`Found ${results.length} team(s) matching "${userName}"`);
        }
      } else if (resourceType === 'storyarc') {
        // Direct story arc search
        results = await searchStoryArc(userName, publisher);
        if (results && results.length > 0) {
          console.log(`Found ${results.length} story arc(s) matching "${userName}"`);
        }
      }
      
      if (results && results.length >= 1) {
        // Show selection dialog for any results (even single result)
        setSearchResults(results);
        setShowDialog(true);
        setSearchDialogOpen(false);
      } else {
        // Show no results notification and create custom entry
        setSearchedName(userName);
        setNoResultsOpen(true);
        // Create a custom entry so the user can still track this character/team/arc
        props.onSubmit([createCustomEntry(userName, publisher, resourceType)]);
        setUserName('');
        setSearchDialogOpen(false);
      }
    } catch (error) {
      console.error('Error searching:', error);
      // Show no results notification on error and create custom entry
      setSearchedName(userName);
      setNoResultsOpen(true);
      props.onSubmit([createCustomEntry(userName, publisher, resourceType)]);
      setUserName('');
      setSearchDialogOpen(false);
    }
  };

  const handleHeroSelect = (hero) => {
    props.onSubmit([hero]);
    setUserName('');
    setSearchResults([]);
    setShowDialog(false);
    setCurrentSearchName('');
    setCurrentPublisher('');
  };

  const handleDialogClose = () => {
    // When cancelled, still create a custom entry so user can load series for this character/team/arc
    // This allows users to track resources even if they don't find an exact match
    if (currentSearchName) {
      props.onSubmit([createCustomEntry(currentSearchName, currentPublisher, currentResourceType)]);
    }
    setShowDialog(false);
    setSearchResults([]);
    setUserName('');
    setCurrentSearchName('');
    setCurrentPublisher('');
    setCurrentResourceType('character');
  };

  return (
    <div>
      {/* FAB button to open search dialog */}
      <div className={classes.fabContainer}>
        <Fab 
          color="primary" 
          className={classes.fab}
          onClick={() => setSearchDialogOpen(true)}
          aria-label="Add hero"
        >
          <AddIcon />
        </Fab>
      </div>
      
      {/* Search Dialog */}
      <Dialog 
        open={searchDialogOpen} 
        onClose={() => setSearchDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            position: 'fixed',
            top: '10%',
            margin: 0
          }
        }}
      >
        <DialogTitle className={classes.dialogTitle}>
          <span>Search Comic Vine</span>
          <IconButton size="small" onClick={() => setSearchDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            variant="outlined"
            label="Resource Type"
            value={resourceType}
            onChange={(e) => setResourceType(e.target.value)}
            className={classes.searchField}
          >
            <MenuItem value="character">Character</MenuItem>
            <MenuItem value="team">Team</MenuItem>
            <MenuItem value="storyarc">Story Arc</MenuItem>
          </TextField>
          <TextField
            select
            fullWidth
            variant="outlined"
            label="Publisher"
            value={publisher}
            onChange={(e) => setPublisher(e.target.value)}
            className={classes.searchField}
          >
            <MenuItem value="">All Publishers</MenuItem>
            <MenuItem value="marvel">Marvel</MenuItem>
            <MenuItem value="dc">DC</MenuItem>
            <MenuItem value="image">Image</MenuItem>
            <MenuItem value="dark horse">Dark Horse</MenuItem>
            <MenuItem value="idw">IDW</MenuItem>
          </TextField>
          <TextField
            autoFocus
            fullWidth
            variant="outlined"
            label={resourceType === 'character' ? 'Character Name' : resourceType === 'team' ? 'Team Name' : 'Story Arc Name'}
            placeholder={`Enter ${resourceType === 'character' ? 'character' : resourceType === 'team' ? 'team' : 'story arc'} name...`}
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSubmit();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSearchDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            color="primary" 
            variant="contained"
            disabled={!userName.trim()}
          >
            Search
          </Button>
        </DialogActions>
      </Dialog>
      
      <HeroSelectionDialog
        open={showDialog}
        heroes={searchResults}
        onSelect={handleHeroSelect}
        onClose={handleDialogClose}
      />
      
      {/* No Results Notification */}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={noResultsOpen}
        autoHideDuration={4000}
        onClose={() => setNoResultsOpen(false)}
      >
        <SnackbarContent
          className={classes.snackbarContent}
          message={
            <span className={classes.snackbarMessage}>
              <InfoIcon className={classes.snackbarIcon} />
              No results found for "{searchedName}"
            </span>
          }
          action={
            <IconButton size="small" color="inherit" onClick={() => setNoResultsOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        />
      </Snackbar>
    </div>
  );
};

class Queryhero extends React.Component {
  render() {
    return (
      <div>
        <Heroes cards={this.props.cards} setCurrentCard={this.props.setCurrentCard} editMode={this.props.editMode} updateHeroImage={this.props.updateHeroImage} updateHero={this.props.updateHero} />
        {this.props.addEnabled && <Form onSubmit={this.props.addNewCard} setProgress={this.props.setProgress} />}
      </div>
    );
  }
}
export default Queryhero;